import { Injectable } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  IHttpConnectionOptions,
  LogLevel
} from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../service/auth.service';

type RawRecord = Record<string, unknown>;

export interface PistaLiveChangedEvent {
  exposicionId: number;
  hash: string;
  utc: string;
  estadosCount: number;
  payload: unknown;
}

export interface JuraLiveChangedEvent {
  exposicionId: number;
  hashEstado: string;
  hashTendenciaOpcional: string;
  updatedUtc: string;
  payload: unknown;
}

interface PistaChangedEventInfo {
  exposicionId: number | null;
  hash: string;
  utc: string;
  estados: RawRecord[];
}

@Injectable({
  providedIn: 'root'
})
export class PistaLiveDebugService {
  private readonly reconnectDelaysMs = [0, 2000, 5000, 10000];
  private readonly debugEnabled = !!environment.pistaLiveDebugEnabled;
  private readonly changedSubject = new Subject<PistaLiveChangedEvent>();
  private readonly juraChangedSubject = new Subject<JuraLiveChangedEvent>();
  private connection: HubConnection | null = null;
  private joinedExpoId: number | null = null;
  private stopInFlight: Promise<void> | null = null;
  public readonly changed$: Observable<PistaLiveChangedEvent> = this.changedSubject.asObservable();
  public readonly juraChanged$: Observable<JuraLiveChangedEvent> = this.juraChangedSubject.asObservable();

  constructor(private readonly authService: AuthService) {}

  public isConnectedToExpo(exposicionId: number | null = null): boolean {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return false;
    }

    if (!this.joinedExpoId) {
      return false;
    }

    const targetExpoId = this.normalizeExpoId(exposicionId);
    if (!targetExpoId) {
      return true;
    }

    return this.joinedExpoId === targetExpoId;
  }

  public async start(exposicionId: number): Promise<void> {
    const nextExpoId = this.normalizeExpoId(exposicionId);
    if (!nextExpoId) {
      return;
    }

    this.ensureConnection();
    if (!this.connection) {
      return;
    }

    const connected = await this.ensureConnected();
    if (!connected) {
      return;
    }

    if (this.joinedExpoId === nextExpoId) {
      return;
    }

    if (this.joinedExpoId) {
      await this.leaveExpo(this.joinedExpoId);
    }

    await this.joinExpo(nextExpoId);
  }

  public async switchExpo(exposicionId: number): Promise<void> {
    const nextExpoId = this.normalizeExpoId(exposicionId);
    if (!nextExpoId) {
      return;
    }

    if (!this.connection) {
      await this.start(nextExpoId);
      return;
    }

    const connected = await this.ensureConnected();
    if (!connected) {
      return;
    }

    if (this.joinedExpoId === nextExpoId) {
      return;
    }

    if (this.joinedExpoId) {
      await this.leaveExpo(this.joinedExpoId);
    }

    await this.joinExpo(nextExpoId);
  }

  public async stop(): Promise<void> {
    if (!this.connection) {
      return;
    }

    if (this.stopInFlight) {
      return this.stopInFlight;
    }

    const stopPromise = this.stopInternal();
    this.stopInFlight = stopPromise;

    try {
      await stopPromise;
    } finally {
      if (this.stopInFlight === stopPromise) {
        this.stopInFlight = null;
      }
    }
  }

  private async stopInternal(): Promise<void> {
    const connection = this.connection;
    const joinedExpoId = this.joinedExpoId;
    this.connection = null;
    this.joinedExpoId = null;

    if (!connection) {
      return;
    }

    if (connection.state === HubConnectionState.Connected && joinedExpoId) {
      try {
        await connection.invoke('LeaveExpo', joinedExpoId);
        this.logDebug(`left expo ${joinedExpoId}`);
      } catch (error) {
        if (!this.isConnectionClosedInvocationError(error)) {
          this.logError(`LeaveExpo(${joinedExpoId}) failed`, error);
        }
      }
    }

    if (connection.state === HubConnectionState.Disconnected) {
      return;
    }

    try {
      await connection.stop();
    } catch (error) {
      this.logError('stop failed', error);
    }
  }

  private ensureConnection(): void {
    if (this.connection) {
      return;
    }

    const apiBase = this.resolveApiBase();
    const connectionOptions = this.buildConnectionOptions();
    const connection = new HubConnectionBuilder()
      .withUrl(`${apiBase}/hubs/pista-live`, connectionOptions)
      .configureLogging(LogLevel.None)
      .withAutomaticReconnect(this.reconnectDelaysMs)
      .build();

    connection.onreconnecting((error) => {
      this.logDebug('reconnecting', error);
    });

    connection.onreconnected((connectionId) => {
      this.logDebug('reconnected', connectionId);

      const rejoinExpoId = this.joinedExpoId;
      if (!rejoinExpoId) {
        return;
      }

      void connection
        .invoke('JoinExpo', rejoinExpoId)
        .then(() => {
          this.changedSubject.next({
            exposicionId: rejoinExpoId,
            hash: '',
            utc: new Date().toISOString(),
            estadosCount: 0,
            payload: null
          });
          this.juraChangedSubject.next({
            exposicionId: rejoinExpoId,
            hashEstado: '',
            hashTendenciaOpcional: '',
            updatedUtc: new Date().toISOString(),
            payload: null
          });
        })
        .catch((error) => {
          this.logError(`rejoin expo ${rejoinExpoId} failed`, error);
        });
    });

    connection.onclose((error) => {
      this.logDebug('closed', error);
    });

    connection.on('pista.changed', (payload: unknown) => {
      this.handlePistaChanged(payload);
    });
    connection.on('jura.changed', (payload: unknown) => {
      this.handleJuraChanged(payload);
    });

    this.connection = connection;
  }

  private buildConnectionOptions(): IHttpConnectionOptions {
    const options: IHttpConnectionOptions = {
      accessTokenFactory: () => this.authService.getAccessToken(),
      withCredentials: false
    };

    if (this.isDirectWebSocketEnabled()) {
      options.transport = HttpTransportType.WebSockets;
      options.skipNegotiation = true;
    }

    return options;
  }

  private isDirectWebSocketEnabled(): boolean {
    return !!environment.FEATURES?.['signalrDirectWebSocket'];
  }

  private async ensureConnected(): Promise<boolean> {
    if (!this.connection) {
      return false;
    }

    if (this.connection.state === HubConnectionState.Connected) {
      return true;
    }

    if (this.connection.state !== HubConnectionState.Disconnected) {
      return false;
    }

    try {
      await this.connection.start();
      this.logDebug('connected', this.connection.connectionId);
      return true;
    } catch (error) {
      this.logError('start failed', error);
      return false;
    }
  }

  private async joinExpo(exposicionId: number): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('JoinExpo', exposicionId);
      this.joinedExpoId = exposicionId;
      this.logDebug(`joined expo ${exposicionId}`);
    } catch (error) {
      this.logError(`JoinExpo(${exposicionId}) failed`, error);
    }
  }

  private async leaveExpo(exposicionId: number): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      if (this.joinedExpoId === exposicionId) {
        this.joinedExpoId = null;
      }
      return;
    }

    try {
      await this.connection.invoke('LeaveExpo', exposicionId);
      this.logDebug(`left expo ${exposicionId}`);
    } catch (error) {
      if (!this.isConnectionClosedInvocationError(error)) {
        this.logError(`LeaveExpo(${exposicionId}) failed`, error);
      }
    } finally {
      if (this.joinedExpoId === exposicionId) {
        this.joinedExpoId = null;
      }
    }
  }

  private logPistaChanged(payload: unknown): void {
    if (!this.debugEnabled) {
      return;
    }

    const eventInfo = this.extractChangedEventInfo(payload);
    const filteredStates = this.filterTargetStates(eventInfo.estados);

    console.log('[pista.live.debug] pista.changed', {
      expo: eventInfo.exposicionId ?? this.joinedExpoId ?? null,
      hash: eventInfo.hash,
      estadosCount: eventInfo.estados.length,
      utc: eventInfo.utc
    });
    console.log('[pista.live.debug] pista.changed estados EN CURSO / EN PISTA', filteredStates);
  }

  private handlePistaChanged(payload: unknown): void {
    const eventInfo = this.extractChangedEventInfo(payload);
    const exposicionId = eventInfo.exposicionId ?? this.joinedExpoId;
    if (exposicionId) {
      this.changedSubject.next({
        exposicionId,
        hash: eventInfo.hash,
        utc: eventInfo.utc,
        estadosCount: eventInfo.estados.length,
        payload
      });
    }

    this.logPistaChanged(payload);
  }

  private handleJuraChanged(payload: unknown): void {
    const record = this.toRecord(payload);
    const nested = record
      ? this.toRecord(this.readRaw(record, ['payload', 'Payload', 'responseObject', 'ResponseObject']))
      : null;
    const source = nested ?? record ?? {};
    const exposicionId =
      this.normalizeExpoId(
        this.readRaw(source, ['exposicionId', 'ExposicionID', 'expoId', 'ExpoId'])
      ) || this.joinedExpoId;

    if (!exposicionId) {
      return;
    }

    this.juraChangedSubject.next({
      exposicionId,
      hashEstado: this.toText(this.readRaw(source, ['hashEstado', 'HashEstado'])),
      hashTendenciaOpcional: this.toText(
        this.readRaw(source, ['hashTendenciaOpcional', 'HashTendenciaOpcional', 'hashTendencia', 'HashTendencia'])
      ),
      updatedUtc: this.toText(
        this.readRaw(source, ['updatedUtc', 'UpdatedUtc', 'utc', 'Utc', 'timestampUtc', 'TimestampUtc'])
      ),
      payload
    });
  }

  private extractChangedEventInfo(payload: unknown): PistaChangedEventInfo {
    const record = this.toRecord(payload);
    const nested = record
      ? this.toRecord(this.readRaw(record, ['payload', 'Payload', 'responseObject', 'ResponseObject']))
      : null;
    const source = nested ?? record ?? {};
    const exposicionId =
      this.normalizeExpoId(
        this.readRaw(source, ['exposicionId', 'ExposicionID', 'expoId', 'ExpoId'])
      ) || null;
    const hash = this.toText(this.readRaw(source, ['hash', 'Hash']));
    const utc = this.toText(
      this.readRaw(source, ['utc', 'Utc', 'updatedUtc', 'UpdatedUtc', 'timestampUtc', 'TimestampUtc'])
    );

    return {
      exposicionId,
      hash,
      utc,
      estados: this.extractStates(payload)
    };
  }

  private extractStates(payload: unknown): RawRecord[] {
    if (Array.isArray(payload)) {
      return payload.filter((item): item is RawRecord => !!item && typeof item === 'object');
    }

    const record = this.toRecord(payload);
    if (!record) {
      return [];
    }

    const direct = this.extractStatesFromRecord(record);
    if (direct.length > 0) {
      return direct;
    }

    const nested = this.toRecord(this.readRaw(record, ['payload', 'Payload', 'responseObject', 'ResponseObject']));
    if (!nested) {
      return [];
    }

    return this.extractStatesFromRecord(nested);
  }

  private extractStatesFromRecord(record: RawRecord): RawRecord[] {
    const candidates = [
      this.readRaw(record, ['estados', 'Estados']),
      this.readRaw(record, ['items', 'Items']),
      this.readRaw(record, ['states', 'States'])
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate.filter((item): item is RawRecord => !!item && typeof item === 'object');
      }
    }

    return [];
  }

  private filterTargetStates(states: RawRecord[]): Array<{
    scope: string;
    identity: string;
    targetLabel: string;
    productoId: number | null;
    rp: string;
    updatedUtc: string;
  }> {
    return states
      .filter((state) =>
        this.isTargetLabel(this.toText(this.readRaw(state, ['targetLabel', 'TargetLabel'])))
      )
      .map((state) => ({
        scope: this.toText(this.readRaw(state, ['scope', 'Scope'])),
        identity: this.toText(this.readRaw(state, ['identity', 'Identity'])),
        targetLabel: this.toText(this.readRaw(state, ['targetLabel', 'TargetLabel'])),
        productoId: this.toNullablePositiveInt(this.readRaw(state, ['productoId', 'ProductoID'])),
        rp: this.toText(this.readRaw(state, ['rp', 'RP'])),
        updatedUtc: this.toText(this.readRaw(state, ['updatedUtc', 'UpdatedUtc', 'actualizadoUtc', 'ActualizadoUtc']))
      }));
  }

  private isTargetLabel(value: string): boolean {
    const normalized = this.normalizeLabel(value);
    return normalized === 'EN CURSO' || normalized === 'EN PISTA';
  }

  private normalizeLabel(value: string): string {
    return value
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  private resolveApiBase(): string {
    const rawBase = (environment.URL_BASE ?? '').trim().replace(/\/+$/, '');
    if (!rawBase) {
      return '';
    }

    if (/^https?:\/\//i.test(rawBase)) {
      try {
        const parsed = new URL(rawBase);
        parsed.search = '';
        parsed.hash = '';
        return parsed.toString().replace(/\/+$/, '');
      } catch {
        // fallback below
      }
    }

    return rawBase.startsWith('/') ? rawBase : `/${rawBase}`;
  }

  private normalizeExpoId(value: unknown): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return 0;
    }

    return Math.trunc(numeric);
  }

  private toNullablePositiveInt(value: unknown): number | null {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return null;
    }

    return Math.trunc(numeric);
  }

  private toText(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim();
  }

  private toRecord(value: unknown): RawRecord | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    return value as RawRecord;
  }

  private readRaw(raw: RawRecord, keys: string[]): unknown {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(raw, key)) {
        return raw[key];
      }
    }
    return null;
  }

  private isConnectionClosedInvocationError(error: unknown): boolean {
    const message =
      (error as { message?: string } | null)?.message ??
      (typeof error === 'string' ? error : '');

    return message
      .toLowerCase()
      .includes('invocation canceled due to the underlying connection being closed');
  }

  private logDebug(message: string, payload?: unknown): void {
    if (!this.debugEnabled) {
      return;
    }

    if (payload !== undefined) {
      console.log(`[pista.live.debug] ${message}`, payload);
      return;
    }

    console.log(`[pista.live.debug] ${message}`);
  }

  private logError(message: string, payload?: unknown): void {
    if (!this.debugEnabled) {
      return;
    }

    if (payload !== undefined) {
      console.error(`[pista.live.debug] ${message}`, payload);
      return;
    }

    console.error(`[pista.live.debug] ${message}`);
  }
}
